/**
 * Weekly under-use as an INFO note (WI 34543, restored info-only on Bug
 * 34652). Bug 34652 made the act-on rule "95%+ projected or it fills before
 * reset", which retired the WI 34543 under-use amber. The signal itself is
 * still useful to Tim on the web Control Center, so it comes back here as a
 * calm note, never an act-on warning: `projectWindow` levels and
 * `buildAlerts` (the act-on list the web Needs attention card and the Roost
 * dashboard share) do not read this file. Pure.
 */
import { AccountSignal, projectWindow } from './attention.logic';
import { WEEK_MS } from './pace.logic';

/** Below this projected-at-reset percent a 7d window is under-using its
 * weekly budget (Tim, WI 34543: 80-100% is the desired cadence). */
export const WEEKLY_PROJECTION_UNDER_PCT = 80;
/** A note only once the week is at least this far gone (WI 34543 follow-up):
 * a scaled-back early pace plus a late burn is the intended shape. */
export const WEEKLY_UNDER_USE_MIN_ELAPSED_FRACTION = 0.7;

export interface UnderUseNoteVm {
  key: string;
  level: 'info';
  text: string;
}

/** True when a 7d projection reads as under-use late enough in the week to say so. */
export function isWeeklyUnderUse(
  projectedAtResetPct: number,
  elapsedFraction: number
): boolean {
  return (
    elapsedFraction >= WEEKLY_UNDER_USE_MIN_ELAPSED_FRACTION &&
    projectedAtResetPct < WEEKLY_PROJECTION_UNDER_PCT
  );
}

/**
 * One info note per account whose live 7d window is under-using its week.
 * Uses the same `projectWindow` projection as every other dashboard number
 * (Bug 34656: same samples, same figure). 5h windows never produce a note.
 */
export function buildUnderUseNotes(
  accounts: AccountSignal[],
  nowMs: number
): UnderUseNoteVm[] {
  const notes: UnderUseNoteVm[] = [];
  for (const a of accounts) {
    const w = a.windows.find((x) => x.window === '7d' && x.resetAtMs > nowMs);
    if (!w) continue;
    const p = projectWindow(w, nowMs);
    if (p.projectedPct === null || p.level !== null) continue;
    const elapsed = (WEEK_MS - (w.resetAtMs - nowMs)) / WEEK_MS;
    if (!isWeeklyUnderUse(p.projectedPct, Math.max(0, Math.min(1, elapsed))))
      continue;
    const name = a.accountUpn.split('@')[0];
    notes.push({
      key: a.accountUpn.toLowerCase(),
      level: 'info',
      text: `${name}: 7d projected at reset ${Math.round(p.projectedPct)}%, under the ${WEEKLY_PROJECTION_UNDER_PCT}-100% cadence`,
    });
  }
  return notes.sort((x, y) => x.key.localeCompare(y.key));
}
